import React, { useState } from 'react';
import axios from 'axios';

const UsersList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [userData, setUserData] = useState([
    {
      id: 1,
      name: 'John Doe',
      email: 'john.doe@example.com',
      role: 'Admin',
      status: 'Active'
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'jane.smith@example.com',
      role: 'User',
      status: 'Active'
    }
  ]);

  const emptyUser = {
    id: 'new',
    name: '',
    email: '',
    role: 'User',
    status: 'Active'
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditingId('new');
    setEditedData(emptyUser);
    setError(null);
    setUserData([emptyUser, ...userData]);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let response;
      if (editingId === 'new') {
        response = await axios.post('/api/users', editedData);
        if (response.status === 201) {
          const updatedData = userData.filter(user => user.id !== 'new');
          setUserData([response.data, ...updatedData]);
          setIsAddingNew(false);
        }
      } else {
        response = await axios.put(`/api/users/${editedData.id}`, editedData);
        if (response.status === 200) {
          const updatedData = userData.map(item => 
            item.id === editedData.id ? response.data : item
          );
          setUserData(updatedData);
        }
      }
      setEditingId(null);
      setEditedData({});
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving changes');
      console.error('Error saving user:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isAddingNew) {
      setUserData(userData.filter(user => user.id !== 'new'));
      setIsAddingNew(false);
    }
    setEditingId(null);
    setEditedData({});
    setError(null);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setEditedData(item);
    setError(null);
  };

  const handleDelete = async (item) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.delete(`/api/users/${item.id}`);
        if (response.status === 200) {
          setUserData(userData.filter(user => user.id !== item.id));
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Error deleting user');
        console.error('Error deleting user:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (field, value) => {
    setEditedData({
      ...editedData,
      [field]: value
    });
    setError(null);
  };

  const filteredData = userData.filter(item =>
    item.id === 'new' || 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mt-4">
      {error && (
        <div className="alert alert-danger mb-4" role="alert">
          {error}
        </div>
      )}
      
      <div className="listing-header d-flex justify-content-between align-items-center">
        <h2 className="listing-title">Users</h2>
        <button 
          className="btn-add-new"
          onClick={handleAddNew}
          disabled={loading || isAddingNew}
        >
          <i className="fas fa-plus-circle"></i>
          <span>Add New User</span>
        </button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          className="form-control listing-search"
          placeholder="Search Users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="listing-table">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th className="text-start">Name</th>
                <th className="text-start">Email</th>
                <th className="text-start">Role</th>
                <th className="text-start">Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item) => (
                <tr key={item.id} className={item.id === 'new' ? 'new-item-row' : ''}>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={editedData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder="Enter name"
                        autoFocus
                      />
                    ) : (
                      item.name
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={editedData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder="Enter email"
                      />
                    ) : (
                      item.email
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.role}
                        onChange={(e) => handleChange('role', e.target.value)}
                      >
                        <option value="Admin">Admin</option>
                        <option value="User">User</option>
                        <option value="Manager">Manager</option>
                      </select>
                    ) : (
                      <span className={`badge bg-${
                        item.role === 'Admin' ? 'primary' : 
                        item.role === 'Manager' ? 'info' : 'secondary'
                      }`}>
                        {item.role}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingId === item.id ? (
                      <select
                        className="form-select form-select-sm"
                        value={editedData.status}
                        onChange={(e) => handleChange('status', e.target.value)}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Pending">Pending</option>
                      </select>
                    ) : (
                      <span className={`badge bg-${
                        item.status === 'Active' ? 'success' : 
                        item.status === 'Inactive' ? 'danger' : 'warning'
                      }`}>
                        {item.status}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons text-end">
                      {editingId === item.id ? (
                        <>
                          <button 
                            className="btn btn-outline-success btn-icon"
                            onClick={handleSave}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-check"></i>
                            )}
                          </button>
                          <button 
                            className="btn btn-outline-secondary btn-icon ms-2"
                            onClick={handleCancel}
                            disabled={loading}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </>
                      ) : (
                        <>
                          <button 
                            className="btn btn-outline-primary btn-icon"
                            onClick={() => handleEdit(item)}
                            disabled={loading}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className="btn btn-outline-danger btn-icon ms-2"
                            onClick={() => handleDelete(item)}
                            disabled={loading}
                          >
                            {loading ? (
                              <i className="fas fa-spinner fa-spin"></i>
                            ) : (
                              <i className="fas fa-trash"></i>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsersList;